<template>
	<v-menu
		v-model="menu"
		:close-on-content-click="false"
		:disabled="disabled"
		offset-y
		transition="scale-transition"
		min-width="23rem"
		max-width="23rem"
	>
		<template v-slot:activator="{ on, attrs }">
			<v-text-field
				ref="field"
				:style="fieldStyle"
				:value="displayText"
				:label="label"
				:rules="rules"
				:disabled="disabled"
				:placeholder="placeholder"
				prepend-inner-icon="mdi-calendar-clock"
				readonly
				outlined
				dense
				hide-details="auto"
				v-bind="attrs"
				v-on="on"
			/>
		</template>

		<v-card class="picker-panel">
			<v-tabs v-model="tab" color="com2Color" grow height="42">
				<v-tab>日期</v-tab>
				<v-tab>時間</v-tab>
			</v-tabs>

			<v-tabs-items v-model="tab">
				<v-tab-item>
					<v-date-picker
						v-model="innerDate"
						color="com1Color"
						locale="zh-tw"
						:min="min"
						:day-format="dayFormat"
						no-title
						full-width
						@input="tab = 1"
					/>
				</v-tab-item>
				<v-tab-item>
					<v-time-picker
						v-model="innerTime"
						color="com1Color"
						format="24hr"
						:use-seconds="useSeconds"
						full-width
					/>
				</v-tab-item>
			</v-tabs-items>

			<v-card-actions class="picker-panel__actions">
				<span class="picker-panel__preview">{{ previewText }}</span>
				<v-spacer />
				<v-btn
					v-if="allowForever"
					small
					color="rgba(41, 75, 113, 1)"
					class="white--text"
					@click="setForever"
				>
					永久
				</v-btn>
				<v-btn
					outlined
					small
					color="lineColor"
					class="picker-cancel-btn"
					@click="menu = false"
				>
					取消
				</v-btn>
				<v-btn
					color="com1Color"
					class="white--text"
					small
					:disabled="!innerDate"
					@click="confirm"
				>
					確定
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script>
export default {
	name: 'MyDateTimePicker',
	props: {
		// 格式：YYYY-MM-DD HH:mm
		value: {
			required: false,
			type: String,
			default: '',
		},
		label: {
			required: false,
			type: String,
			default: '',
		},
		placeholder: {
			required: false,
			type: String,
			default: '',
		},
		rules: {
			required: false,
			type: Array,
			default: () => [],
		},
		disabled: {
			required: false,
			type: Boolean,
			default: false,
		},
		// 可選日期下限（YYYY-MM-DD），用於結束不可早於開始
		min: {
			required: false,
			type: String,
			default: undefined,
		},
		// 欄位寬度（例如 '210px'）
		width: {
			required: false,
			type: String,
			default: undefined,
		},
		// 是否在下拉內提供「永久（不設結束）」選項；選擇後 value 為 null
		// 基礎元件預設不開啟，由使用端自行決定
		allowForever: {
			required: false,
			type: Boolean,
			default: false,
		},
		// 是否「可手選」秒數；預設 false（值格式 HH:mm，開啟後為 HH:mm:ss）
		useSeconds: {
			required: false,
			type: Boolean,
			default: false,
		},
		// 「只顯示」用的固定秒數（如 '00' / '59'）：僅影響欄位/底部呈現，
		// 不可手選、也不寫入 value（value 維持 HH:mm，秒數由使用端自行決定）
		displaySeconds: {
			required: false,
			type: String,
			default: '',
		},
	},
	model: {
		prop: 'value',
		event: 'input',
	},
	data() {
		return {
			menu: false,
			tab: 0,
			innerDate: '',
			innerTime: '00:00',
		}
	},
	computed: {
		previewText() {
			if (this.innerDate) {
				return this.withDisplaySeconds(`${this.innerDate} ${this.innerTime}`)
			}
			// 目前是永久且尚未改選日期 → 顯示「永久」，而非「尚未選擇」
			return this.isForever ? '永久' : '尚未選擇'
		},
		fieldStyle() {
			return this.width ? { width: this.width, maxWidth: '100%' } : {}
		},
		// 依是否含秒數決定預設/缺省的時間字串
		defaultTime() {
			return this.useSeconds ? '00:00:00' : '00:00'
		},
		// value === null 且允許永久 → 視為「永久（不設結束）」
		isForever() {
			return this.allowForever && this.value === null
		},
		// 欄位顯示文字：永久顯示「永久」；其餘照值顯示，並補上「只顯示用」的固定秒數
		displayText() {
			if (this.isForever) {
				return '永久'
			}
			return this.withDisplaySeconds(this.value || '')
		},
	},
	watch: {
		menu(open) {
			if (open) {
				this.tab = 0
				this.syncFromValue()
			}
		},
		value() {
			this.syncFromValue()
		},
	},
	created() {
		this.syncFromValue()
	},
	methods: {
		dayFormat(date) {
			// 只顯示日期數字（避免 zh-tw 的「17日」把月曆撐寬）
			return String(Number(date.split('-')[2]))
		},
		syncFromValue() {
			if (this.value) {
				const [d, t] = this.value.split(' ')
				this.innerDate = d || ''
				this.innerTime = t || this.defaultTime
			} else {
				this.innerDate = ''
				this.innerTime = this.defaultTime
			}
		},
		confirm() {
			if (!this.innerDate) {
				return
			}
			this.$emit(
				'input',
				`${this.innerDate} ${this.innerTime || this.defaultTime}`,
			)
			this.menu = false
		},
		// 選擇「永久（不設結束）」：以 null 代表，並關閉下拉
		setForever() {
			this.$emit('input', null)
			this.menu = false
		},
		// 只在「顯示」時補上固定秒數（value 維持 HH:mm，不被改動）
		withDisplaySeconds(text) {
			if (!this.displaySeconds || !text) {
				return text
			}
			const [d, t = ''] = text.split(' ')
			const parts = t.split(':')
			// 已含秒數則不重複補
			if (parts.length >= 3) {
				return text
			}
			return `${d} ${parts[0] || '00'}:${parts[1] || '00'}:${
				this.displaySeconds
			}`
		},
		// 供使用端在「相依欄位」變動後強制重驗（例如區間的開始改動、結束需重新比對）。
		// vuetify 的 rules watcher 以 deepEqual 比對，而 deepEqual 對任兩個函式都判定相等，
		// 因此無法靠「傳入新的規則函式」觸發重驗，只能由外部主動呼叫。
		validate() {
			return this.$refs.field?.validate()
		},
	},
}
</script>
